import React, { useState } from 'react';

import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Button } from '../../components/ui/Button/Button';
import { EmptyState } from '../../components/ui/EmptyState/EmptyState';
import { TableFooter } from '../../components/ui/TableFooter/TableFooter';
import { useToast } from '../../components/ui/Toast/ToastProvider';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { TrashBinIcon } from '../../assets/icons/icons';
import { Modal, ModalActions } from '../../components/ui/Modal/Modal';
import {
    useCreateNotificationMutation,
    useGetNotificationsQuery,
    useRemoveNotificationMutation,
    useUpdateNotificationMutation,
} from '../../entities/notifications/api/notificationsApi';
import type {
    AppNotification,
    NotificationForm,
    NotificationSortKey,
    NotificationStatus,
} from '../../entities/notifications/model/types';
import { formatYekb } from '../../entities/notifications/model/window';
import { NotificationModal } from '../../components/NotificationModal/NotificationModal';
import { formatRangeLabel } from '../../utils/format';
import { getErrorMessage } from '../../shared/api/errors';
import styles from './NotificationsPage.module.scss';
import { DataTable, tableStyles } from '../../components/ui/DataTable/DataTable';
import { SortableTh } from '../../components/ui/DataTable/SortableTh';
import { SearchField } from '../../components/ui/SearchField/SearchField';

const STATUS_LABEL: Record<NotificationStatus, string> = {
    planned: 'Запланировано',
    active: 'Активно',
    done: 'Завершено',
    unknown: 'Без срока',
};

const EMPTY = 'Не указано';

/* Время приходит строками в GMT+5 без смещения: new Date() читал бы их как локальные
   и в Москве показывал на 2 часа раньше. Форматируем метки как есть. */
const showStamp = (stamp: string): string => formatYekb(stamp) || EMPTY;

export function NotificationsPage() {
    const toast = useToast();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState<NotificationSortKey>('created_at');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [mode, setMode] = useState<'new' | 'edit' | 'copy' | null>(null);
    const [current, setCurrent] = useState<AppNotification | null>(null);
    const [removing, setRemoving] = useState<AppNotification | null>(null);

    const query = useDebouncedValue(search, 400);

    const { data, isLoading, isFetching, isError, refetch } = useGetNotificationsQuery({
        pageNum: page,
        pageSize,
        query,
        sortBy,
        sortOrder,
    });

    const [createNotification, { isLoading: isCreating }] = useCreateNotificationMutation();
    const [updateNotification, { isLoading: isUpdating }] = useUpdateNotificationMutation();
    const [removeNotification, { isLoading: isDeleting }] = useRemoveNotificationMutation();

    const items = data?.items ?? [];
    const total = data?.total ?? 0;
    const pageCount = Math.max(1, data?.pages ?? 1);

    const changePage = (next: number) => setPage(Math.min(Math.max(1, next), pageCount));

    const changePageSize = (next: number) => {
        setPageSize(next);
        setPage(1);
    };

    const toggleSort = (key: NotificationSortKey) => {
        if (sortBy === key) setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        else {
            setSortBy(key);
            setSortOrder('desc');
        }
        setPage(1);
    };

    const open = (next: 'new' | 'edit' | 'copy', item: AppNotification | null = null) => {
        setCurrent(item);
        setMode(next);
    };

    const handleSubmit = async (form: NotificationForm) => {
        try {
            /* Копия — обычное создание с заполненными полями: ручку copy_info_message
               использовать нельзя, она делает запись без дат окна. */
            if (mode === 'edit' && current) {
                /* PUT пересоздаёт запись: у объявления меняется id, и пользователям
                   оно покажется заново. Обычно это и нужно — текст-то изменился. */
                await updateNotification({ id: current.id, form }).unwrap();
                toast('Уведомление отредактировано');
            } else {
                await createNotification(form).unwrap();
                toast(form.showNow ? 'Уведомление создано и уже показывается' : 'Уведомление создано');
            }
            setMode(null);
        } catch (error) {
            /* 400 с текстом про формат даты — единственная содержательная ошибка.
               Пересечение окон больше не проверяется, 409 не приходит. */
            toast(getErrorMessage(error, 'Не удалось сохранить уведомление'));
        }
    };

    const handleRemove = async () => {
        if (!removing) return;
        try {
            await removeNotification(removing.id).unwrap();
            toast('Уведомление удалено');
            setRemoving(null);
        } catch (error) {
            toast(getErrorMessage(error, 'Не удалось удалить уведомление'));
        }
    };

    return (
        <>
            <PageHeader
                title="Уведомления"
                actions={
                    <Button variant="primary" onClick={() => open('new')}>
                        Создать уведомление
                    </Button>
                }
            />

            <section className={styles.panel}>
                <SearchField
                    value={search}
                    placeholder="Найти уведомление"
                    onChange={(value) => {
                        setSearch(value);
                        setPage(1);
                    }}
                />

                {isError ? (
                    <div className={styles.state}>
                        <p>Не удалось загрузить уведомления.</p>
                        <Button onClick={() => refetch()}>Повторить</Button>
                    </div>
                ) : (
                    <DataTable
                        static
                        loading={isFetching}
                        isLoading={isLoading}
                        loadingText="Загружаем уведомления…"
                        scrollKey={`${page}-${pageSize}-${query}-${sortBy}-${sortOrder}`}
                        footer={
                            total > 0 ? (
                                <TableFooter
                                    page={page}
                                    totalPages={pageCount}
                                    rangeLabel={formatRangeLabel(page, pageSize, total)}
                                    onPageChange={changePage}
                                />
                            ) : undefined
                        }
                        empty={
                            items.length === 0 ? (
                                <EmptyState
                                    title="Уведомлений пока нет"
                                    description="Создайте уведомление, чтобы предупредить пользователей о работах."
                                    action={{ label: 'Создать уведомление', onClick: () => open('new') }}
                                />
                            ) : undefined
                        }
                    >
                        <thead>
                            <tr>
                                <SortableTh
                                    title="Создано"
                                    active={sortBy === 'created_at'}
                                    dir={sortOrder}
                                    onClick={() => toggleSort('created_at')}
                                />
                                <th>Текст</th>
                                <SortableTh
                                    title="Начало"
                                    active={sortBy === 'start_date'}
                                    dir={sortOrder}
                                    onClick={() => toggleSort('start_date')}
                                />
                                <SortableTh
                                    title="Завершение"
                                    active={sortBy === 'end_date'}
                                    dir={sortOrder}
                                    onClick={() => toggleSort('end_date')}
                                />
                                <th>Статус</th>
                                <th>Автор</th>
                                <th className={tableStyles.actions} aria-label="Действия" />
                            </tr>
                        </thead>

                        <tbody>
                            {items.map((item) => (
                                <tr key={item.id}>
                                    <td>{showStamp(item.createdAtStamp)}</td>
                                    <td>
                                        <span className={styles.text} title={item.text}>
                                            {item.text}
                                        </span>
                                    </td>
                                    <td>{showStamp(item.startsAt)}</td>
                                    <td>{showStamp(item.endsAt)}</td>
                                    <td>
                                        <span className={styles.pill} data-status={item.status}>
                                            {STATUS_LABEL[item.status]}
                                        </span>
                                    </td>
                                    <td>{item.author}</td>
                                    <td className={styles.actions}>
                                        <div className={styles.actionsInner}>
                                            <button type="button" onClick={() => open('edit', item)}>Изменить</button>
                                            <button type="button" onClick={() => open('copy', item)}>Копировать</button>
                                            <button type="button" className={styles.danger} onClick={() => setRemoving(item)}>Удалить</button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </DataTable>
                )}
            </section>

            <Modal
                open={removing !== null}
                width={420}
                tone="danger"
                title="Удалить уведомление?"
                description={
                    removing
                        ? 'Уведомление перестанет показываться пользователям. Восстановить запись нельзя.'
                        : undefined
                }
                icon={<TrashBinIcon />}
                onClose={() => setRemoving(null)}
                footer={
                    <ModalActions>
                        <Button onClick={() => setRemoving(null)} disabled={isDeleting}>
                            Отмена
                        </Button>
                        <Button variant="danger" onClick={handleRemove} disabled={isDeleting}>
                            {isDeleting ? 'Удаляем…' : 'Удалить'}
                        </Button>
                    </ModalActions>
                }
            />

            <NotificationModal
                mode={mode}
                source={mode === 'new' ? null : current}
                saving={isCreating || isUpdating}
                onClose={() => setMode(null)}
                onSubmit={handleSubmit}
            />
        </>
    );
}